import { createFileRoute } from '@tanstack/react-router'
import { useState, useMemo, type CSSProperties } from 'react'
import { useQuery as useConvexQuery } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import type { Doc, Id } from 'convex/_generated/dataModel'

dayjs.extend(isoWeek)

export const Route = createFileRoute('/app/schedule/calendar')({
  component: CalendarView,
})

function CalendarView() {
  // Get authenticated user from Clerk/Convex
  const currentUser = useCurrentUser()

  // Show loading state while user is being fetched
  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Please sign in to view calendar</div>
      </div>
    )
  }

  return <CalendarViewContent userId={currentUser._id} />
}

function CalendarViewContent({ userId }: { userId: Id<'users'> }) {
  const [currentDate, setCurrentDate] = useState(dayjs())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedReviews, setSelectedReviews] = useState<Doc<'vocabularyItems'>[]>([])
  const [selectedFlashcards, setSelectedFlashcards] = useState<Doc<'flashcards'>[]>([])

  // Calculate date range for current month
  const startOfMonth = currentDate.startOf('month')
  const endOfMonth = currentDate.endOf('month')

  // Query upcoming reviews for the month
  const upcomingReviews = useConvexQuery(api.vocabulary.getUpcomingReviews, {
    userId,
    startDate: startOfMonth.valueOf(),
    endDate: endOfMonth.valueOf(),
  })

  // Query study actions for the month
  const studyActions = useConvexQuery(api.studyActions.getByDateRange, {
    userId,
    startDate: startOfMonth.valueOf(),
    endDate: endOfMonth.valueOf(),
  })

  // Query upcoming flashcards for the month (with deck metadata and active filter)
  const upcomingFlashcards = useConvexQuery(api.flashcards.getUpcomingFlashcardsGroupedByDeck, {
    userId,
    startDate: startOfMonth.valueOf(),
    endDate: endOfMonth.valueOf(),
    onlyActiveDecks: true,
  })

  // Mutations for study actions (reserved for future interactive calendar features)
  // const createStudyAction = useMutation(api.studyActions.createStudyAction)
  // const updateStatus = useMutation(api.studyActions.updateStatus)
  // const deleteStudyAction = useMutation(api.studyActions.deleteStudyAction)

  // Group reviews by date
  const reviewsByDate = useMemo(() => {
    if (!upcomingReviews) return {}

    const grouped: Record<string, Doc<'vocabularyItems'>[]> = {}

    for (const review of upcomingReviews) {
      const dateKey = dayjs(review.dueDate).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(review)
    }

    return grouped
  }, [upcomingReviews])

  // Group study actions by date
  const actionsByDate = useMemo(() => {
    if (!studyActions) return {}

    const grouped: Record<string, Doc<'studyActions'>[]> = {}

    for (const action of studyActions) {
      if (!action.scheduledDate) continue
      const dateKey = dayjs(action.scheduledDate).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(action)
    }

    return grouped
  }, [studyActions])

  // Group flashcards by date
  const flashcardsByDate = useMemo(() => {
    if (!upcomingFlashcards) return {}

    const grouped: Record<string, Doc<'flashcards'>[]> = {}

    for (const flashcard of upcomingFlashcards) {
      const dateKey = dayjs(flashcard.dueDate).format('YYYY-MM-DD')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(flashcard)
    }

    return grouped
  }, [upcomingFlashcards])

  // Extract unique active decks with their colors for legend
  const activeDecks = useMemo(() => {
    if (!upcomingFlashcards) return []

    const deckMap = new Map<string, { name: string; color: string }>()

    for (const flashcard of upcomingFlashcards) {
      const deckName = flashcard.deckName ?? 'Unknown'
      const deckColor = flashcard.deckColor ?? '#0ea5e9'

      if (!deckMap.has(deckName)) {
        deckMap.set(deckName, { name: deckName, color: deckColor })
      }
    }

    return Array.from(deckMap.values())
  }, [upcomingFlashcards])

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'))
    setSelectedDate(null)
    setSelectedReviews([])
  }

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'))
    setSelectedDate(null)
    setSelectedReviews([])
  }

  const handleToday = () => {
    setCurrentDate(dayjs())
    setSelectedDate(null)
    setSelectedReviews([])
  }

  const handleDateClick = (dateKey: string) => {
    const reviews = reviewsByDate[dateKey] || []
    const flashcards = flashcardsByDate[dateKey] || []
    setSelectedDate(dateKey)
    setSelectedReviews(reviews)
    setSelectedFlashcards(flashcards)
  }

  const handleCloseSidebar = () => {
    setSelectedDate(null)
    setSelectedReviews([])
    setSelectedFlashcards([])
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              Scheduling
            </h1>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <a
                href="/app/schedule/calendar"
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-terminal-green text-white font-bold font-mono text-xs sm:text-sm tracking-wider hover:bg-terminal-green/80 transition-all border-2 border-terminal-green text-center"
              >
                [CALENDAR VIEW]
              </a>
              <a
                href="/app/schedule/analytics"
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-sky-blue text-white font-bold font-mono text-xs sm:text-sm tracking-wider hover:bg-sky-blue/80 transition-all border-2 border-sky-blue text-center"
              >
                [ANALYTICS]
              </a>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Calendar Navigation */}
          <div className={`flex-1 bg-gray-900 rounded-lg shadow-md border-2 border-sky-blue p-6 ${selectedDate ? 'lg:w-2/3' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handlePrevMonth}
                className="px-4 py-2 bg-sky-blue hover:bg-terminal-green text-white rounded-lg transition-colors font-bold border-2 border-sky-blue"
              >
                ← Previous
              </button>

              <div className="text-center">
                <h2 className="text-2xl font-bold text-white">
                  {currentDate.format('MMMM YYYY')}
                </h2>
                <button
                  onClick={handleToday}
                  className="text-sm text-sky-blue hover:text-terminal-green hover:underline mt-1 font-bold"
                >
                  Today
                </button>
              </div>

              <button
                onClick={handleNextMonth}
                className="px-4 py-2 bg-sky-blue hover:bg-terminal-green text-white rounded-lg transition-colors font-bold border-2 border-sky-blue"
              >
                Next →
              </button>
            </div>

            {/* Calendar Grid */}
            <CalendarGrid
              currentDate={currentDate}
              reviewsByDate={reviewsByDate}
              actionsByDate={actionsByDate}
              flashcardsByDate={flashcardsByDate}
              onDateClick={handleDateClick}
              selectedDate={selectedDate}
            />
          </div>

          {/* Right Sidebar - Review Preview */}
          {selectedDate && (
            <ReviewSidebar
              date={selectedDate}
              reviews={selectedReviews}
              flashcards={selectedFlashcards}
              onClose={handleCloseSidebar}
              isPreview={false}
            />
          )}
        </div>

        {/* Legend */}
        <div className="bg-gray-900 rounded-lg shadow-md border-2 border-sky-blue p-6 mt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Legend</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 border-2 border-sky-blue rounded" style={{ backgroundColor: '#0ea5e9' }} />
              <span className="text-sm text-white">Today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: '#166534' }} />
              <span className="text-sm text-white">1-5 items</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: '#854d0e' }} />
              <span className="text-sm text-white">6-10 items</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded" style={{ backgroundColor: '#991b1b' }} />
              <span className="text-sm text-white">11+ items</span>
            </div>
          </div>

          {/* Active Decks Section */}
          {activeDecks.length > 0 && (
            <div className="mt-4 pt-4 border-t border-sky-blue/30">
              <h4 className="text-sm font-semibold text-white mb-3">Active Decks</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {activeDecks.map((deck) => (
                  <div key={deck.name} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full border border-sky-blue flex-shrink-0"
                      style={{ backgroundColor: deck.color }}
                    />
                    <span className="text-sm text-white truncate">{deck.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-sky-blue/30">
            <p className="text-xs text-gray-400 mb-2">
              Calendar shows both vocabulary reviews (gray) and study actions (blue)
            </p>
            <div className="flex gap-4">
              <div className="flex items-center gap-1">
                <span className="text-xs px-1.5 py-0.5 rounded bg-green-700 text-white">completed</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-700 text-white">in_progress</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-white">pending</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function CalendarGrid({
  currentDate,
  reviewsByDate,
  actionsByDate,
  flashcardsByDate,
  onDateClick,
  selectedDate,
}: {
  currentDate: dayjs.Dayjs
  reviewsByDate: Record<string, Doc<'vocabularyItems'>[]>
  actionsByDate: Record<string, Doc<'studyActions'>[]>
  flashcardsByDate: Record<string, Doc<'flashcards'>[]>
  onDateClick: (dateKey: string) => void
  selectedDate: string | null
}) {
  const today = dayjs().format('YYYY-MM-DD')
  const startOfMonth = currentDate.startOf('month')
  const endOfMonth = currentDate.endOf('month')

  // Calculate calendar grid (6 weeks to show full month)
  const startDay = startOfMonth.day() // 0-6 (Sunday-Saturday)
  const daysInMonth = endOfMonth.date()

  // Generate all days in calendar grid
  const calendarDays: Array<{ date: dayjs.Dayjs; isCurrentMonth: boolean }> = []

  // Previous month days
  const prevMonthEnd = startOfMonth.subtract(1, 'day')
  for (let i = startDay - 1; i >= 0; i--) {
    calendarDays.push({
      date: prevMonthEnd.subtract(i, 'day'),
      isCurrentMonth: false,
    })
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push({
      date: startOfMonth.date(i),
      isCurrentMonth: true,
    })
  }

  // Next month days to fill grid (up to 35 days total = 5 weeks)
  const remainingDays = 35 - calendarDays.length
  for (let i = 1; i <= remainingDays; i++) {
    calendarDays.push({
      date: endOfMonth.add(i, 'day'),
      isCurrentMonth: false,
    })
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-xs sm:text-sm font-semibold text-white py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className="grid grid-cols-7 gap-2 sm:gap-1">
        {calendarDays.map((day, index) => {
          const dateKey = day.date.format('YYYY-MM-DD')
          const reviews = reviewsByDate[dateKey] || []
          const actions = actionsByDate[dateKey] || []
          const flashcards = flashcardsByDate[dateKey] || []
          const reviewCount = reviews.length
          const actionCount = actions.length
          const flashcardCount = flashcards.length
          const totalItems = reviewCount + actionCount + flashcardCount
          const isToday = dateKey === today

          const isSelected = selectedDate === dateKey

          return (
            <CalendarDay
              key={index}
              date={day.date}
              isCurrentMonth={day.isCurrentMonth}
              isToday={isToday}
              isSelected={isSelected}
              reviewCount={reviewCount}
              actionCount={actionCount}
              flashcardCount={flashcardCount}
              reviews={reviews}
              actions={actions}
              flashcards={flashcards}
              onClick={() => totalItems > 0 && onDateClick(dateKey)}
            />
          )
        })}
      </div>
    </div>
  )
}

function CalendarDay({
  date,
  isCurrentMonth,
  isToday,
  isSelected,
  reviewCount,
  actionCount,
  flashcardCount,
  reviews,
  actions,
  flashcards,
  onClick,
}: {
  date: dayjs.Dayjs
  isCurrentMonth: boolean
  isToday: boolean
  isSelected: boolean
  reviewCount: number
  actionCount: number
  flashcardCount: number
  reviews: Doc<'vocabularyItems'>[]
  actions: Doc<'studyActions'>[]
  flashcards: (Doc<'flashcards'> & { deckColor?: string; deckName?: string; isSystemDeck?: boolean })[]
  onClick: () => void
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const totalItems = reviewCount + actionCount + flashcardCount

  // Group flashcards by deck and get unique deck colors
  const deckGroups = useMemo(() => {
    const groups = new Map<string, { color: string; count: number; name: string }>()

    for (const flashcard of flashcards) {
      const deckName = flashcard.deckName ?? 'Unknown'
      const deckColor = flashcard.deckColor ?? '#0ea5e9'

      const existing = groups.get(deckName)
      if (existing) {
        existing.count++
      } else {
        groups.set(deckName, { color: deckColor, count: 1, name: deckName })
      }
    }

    return Array.from(groups.values())
  }, [flashcards])

  // Determine background intensity based on total items
  const getBackgroundStyle = (): CSSProperties => {
    if (totalItems === 0) return { backgroundColor: '#ffffff' }
    if (totalItems <= 5) return { backgroundColor: '#f0fdf4' } // green-50
    if (totalItems <= 10) return { backgroundColor: '#fef9c3' } // yellow-50
    return { backgroundColor: '#fee2e2' } // red-50
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        className={`
          aspect-square rounded-lg border-2 transition-all cursor-pointer
          ${isToday ? 'border-blue-500' : 'border-gray-200'}
          ${isSelected ? 'ring-2 ring-blue-500' : ''}
          ${!isCurrentMonth ? 'opacity-40' : ''}
          hover:shadow-md
        `}
        style={getBackgroundStyle()}
        onClick={onClick}
      >
        <div className="p-1.5 sm:p-1 h-full flex flex-col">
          <div className={`text-xs sm:text-sm font-semibold ${!isCurrentMonth ? 'text-gray-400' : 'text-blue-800'}`}>
            {date.date()}
          </div>
          {totalItems > 0 && (
            <div className="flex-1 flex flex-col items-center justify-center gap-0.5">
              {reviewCount > 0 && (
                <div className="flex flex-col items-center leading-none">
                  <span className="text-sm sm:text-base font-bold text-gray-700">{reviewCount}</span>
                  <span className="text-[8px] sm:text-[10px] text-gray-600">reviews</span>
                </div>
              )}
              {flashcardCount > 0 && (
                <div className="flex flex-col items-center leading-none">
                  <span className="text-sm sm:text-base font-bold text-green-700">{flashcardCount}</span>
                </div>
              )}
              {actionCount > 0 && (
                <div className="flex flex-col items-center leading-none">
                  <span className="text-sm sm:text-base font-bold text-blue-700">{actionCount}</span>
                  <span className="text-[8px] sm:text-[10px] text-blue-600">actions</span>
                </div>
              )}
              {/* Deck Color Indicators */}
              {deckGroups.length > 0 && (
                <div className="flex gap-0.5 flex-wrap justify-center">
                  {deckGroups.map((deck) => (
                    <div
                      key={deck.name}
                      className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full border border-gray-400"
                      style={{ backgroundColor: deck.color }}
                      title={`${deck.name}: ${deck.count} cards`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && totalItems > 0 && (
        <div className="absolute z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg">
          <div className="font-semibold mb-2">
            {date.format('MMMM D, YYYY')}
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {/* Study Actions */}
            {actions.length > 0 && (
              <div>
                <div className="text-blue-300 font-semibold mb-1">Study Actions:</div>
                {actions.slice(0, 5).map((action) => (
                  <div key={action._id} className="border-t border-gray-700 pt-1 pl-2">
                    <div className="flex items-start gap-1">
                      <span className={`px-1 rounded text-[10px] ${
                        action.status === 'completed' ? 'bg-green-700' :
                        action.status === 'in_progress' ? 'bg-yellow-700' :
                        action.status === 'cancelled' ? 'bg-red-700' :
                        'bg-gray-700'
                      }`}>
                        {action.status}
                      </span>
                      <span className="flex-1">{action.title}</span>
                    </div>
                  </div>
                ))}
                {actions.length > 5 && (
                  <div className="text-gray-400 italic pl-2">
                    +{actions.length - 5} more actions...
                  </div>
                )}
              </div>
            )}

            {/* Vocabulary Reviews */}
            {reviews.length > 0 && (
              <div>
                <div className="text-gray-300 font-semibold mb-1">Vocabulary Due:</div>
                {reviews.slice(0, 5).map((review) => (
                  <div key={review._id} className="border-t border-gray-700 pt-1 pl-2">
                    • {review.word}
                  </div>
                ))}
                {reviews.length > 5 && (
                  <div className="text-gray-400 italic pl-2">
                    +{reviews.length - 5} more reviews...
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Tooltip arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-8 border-transparent border-t-gray-900" />
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewSidebar({
  date,
  reviews,
  flashcards = [],
  onClose,
  isPreview,
}: {
  date: string
  reviews: Doc<'vocabularyItems'>[]
  flashcards?: Doc<'flashcards'>[]
  onClose: () => void
  isPreview: boolean
}) {
  const reviewUrl = isPreview ? '/preview/schedule/review' : '/app/schedule/review'
  const flashcardUrl = '/app/flashcards'
  const totalItems = reviews.length + flashcards.length

  return (
    <>
      {/* Mobile: Bottom drawer overlay backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Desktop: Right sidebar | Mobile: Bottom drawer */}
      <div className="
        fixed bottom-0 left-0 right-0
        lg:static lg:w-1/3
        bg-gray-900 rounded-t-lg lg:rounded-lg shadow-md border-2 border-sky-blue
        p-4 sm:p-6
        z-50 lg:z-auto
        max-h-[40vh] lg:max-h-[calc(100vh-4rem)] lg:sticky lg:top-8
        overflow-y-auto
      ">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-white">
            {dayjs(date).format('MMMM D, YYYY')}
          </h2>
          <button
            onClick={onClose}
            className="text-sky-blue hover:text-terminal-green text-2xl leading-none"
            aria-label="Close sidebar"
          >
            ×
          </button>
        </div>

      <div className="mb-3 sm:mb-4">
        <p className="text-gray-300 text-xs sm:text-sm mb-2">
          {totalItems} {totalItems === 1 ? 'item' : 'items'} due for review
        </p>
        {reviews.length > 0 && (
          <p className="text-gray-400 text-[10px] sm:text-xs">
            • {reviews.length} {reviews.length === 1 ? 'vocabulary item' : 'vocabulary items'}
          </p>
        )}
        {flashcards.length > 0 && (
          <p className="text-terminal-green text-[10px] sm:text-xs">
            • {flashcards.length} {flashcards.length === 1 ? 'flashcard' : 'flashcards'}
          </p>
        )}
      </div>

      {/* Vocabulary Cards Preview */}
      {reviews.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-xs sm:text-sm font-semibold text-white mb-2">Vocabulary Items</h3>
          <div className="space-y-2 sm:space-y-3">
            {reviews.map((review) => (
              <div
                key={review._id}
                className="border border-sky-blue/30 rounded-lg p-2 sm:p-4 bg-gray-800"
              >
                <div className="font-semibold text-sm sm:text-base text-white mb-1">
                  {review.word}
                </div>
                {review.translation && (
                  <div className="text-xs sm:text-sm text-gray-300 mb-1 sm:mb-2">
                    {review.translation}
                  </div>
                )}
                {review.context && (
                  <div className="text-[10px] sm:text-xs text-gray-400 italic">
                    &quot;{review.context}&quot;
                  </div>
                )}
                {review.partOfSpeech && (
                  <div className="text-[10px] sm:text-xs text-sky-blue mt-1">
                    ({review.partOfSpeech})
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flashcards Preview */}
      {flashcards.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <h3 className="text-xs sm:text-sm font-semibold text-terminal-green mb-2">Flashcards</h3>
          <div className="space-y-2 sm:space-y-3">
            {flashcards.slice(0, 5).map((flashcard) => (
              <div
                key={flashcard._id}
                className="border border-terminal-green/30 rounded-lg p-2 sm:p-4 bg-gray-800"
              >
                <div className="text-xs sm:text-sm text-white mb-1">
                  {flashcard.frontSpanish[0]}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-300">
                  {flashcard.backEnglish[0]}
                </div>
                {flashcard.targetVerb && (
                  <div className="text-[10px] sm:text-xs text-terminal-green mt-1">
                    Target verb: {flashcard.targetVerb}
                  </div>
                )}
              </div>
            ))}
            {flashcards.length > 5 && (
              <div className="text-[10px] sm:text-xs text-gray-400 italic text-center">
                +{flashcards.length - 5} more flashcards...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Buttons */}
      <div className="space-y-2 sm:space-y-3">
        {/* Vocabulary Review Button */}
        {reviews.length > 0 && (
          <a
            href={reviewUrl}
            className="block w-full px-4 sm:px-6 py-2 sm:py-3 bg-sky-blue hover:bg-terminal-green text-white font-bold text-center rounded-lg transition-colors border-2 border-sky-blue text-sm sm:text-base"
          >
            Review Vocabulary ({reviews.length} {reviews.length === 1 ? 'card' : 'cards'})
          </a>
        )}

        {/* Flashcard Review Button */}
        {flashcards.length > 0 && (
          <a
            href={flashcardUrl}
            className="block w-full px-4 sm:px-6 py-2 sm:py-3 bg-terminal-green hover:bg-sky-blue text-white font-bold text-center rounded-lg transition-colors border-2 border-terminal-green text-sm sm:text-base"
          >
            Review Flashcards ({flashcards.length} {flashcards.length === 1 ? 'card' : 'cards'})
          </a>
        )}
      </div>
      </div>
    </>
  )
}
